import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';

// Minimal type declaration for gtag so TS doesn't complain.
// The function is defined in index.html before React hydrates.
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Small delay so it doesn't pop up instantly on first paint
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookie-consent', 'all');
    setIsVisible(false);
    // Grant all tracking consent via GA4 Consent Mode v2
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'analytics_storage': 'granted',
        'personalization_storage': 'granted',
      });
    }
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('cookie-consent', 'necessary');
    setIsVisible(false);
    // Explicitly keep denied — defaults in index.html already handle this,
    // but we call update anyway so the choice is recorded for GA4 audit logs.
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'denied',
        'personalization_storage': 'denied',
      });
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[999] p-4 md:p-6 pointer-events-none"
        >
          <div className="max-w-5xl mx-auto bg-charcoal text-white rounded-2xl shadow-2xl border border-white/10 p-6 md:p-8 pointer-events-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden">
            {/* Background Glow */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-steel-blue/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
            
            <div className="flex items-start gap-4 max-w-2xl relative z-10">
              <div className="w-12 h-12 bg-steel-blue/20 rounded-xl flex items-center justify-center shrink-0">
                <Cookie className="w-6 h-6 text-steel-blue" />
              </div>
              <div>
                <h3 className="text-lg font-bold mb-2">Vi respekterer dit privatliv</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Vi bruger cookies for at forbedre din oplevelse på vores hjemmeside, analysere trafik og tilpasse indhold. 
                  Du kan vælge at acceptere alle cookies eller kun de strengt nødvendige. Læs mere i vores <Link to="/privatlivspolitik" className="text-steel-blue hover:text-white transition-colors underline underline-offset-2">privatlivspolitik</Link>.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0 relative z-10">
              <button 
                onClick={handleAcceptNecessary}
                className="px-6 py-3 rounded-xl text-sm font-bold text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
              >
                Kun nødvendige
              </button>
              <button 
                onClick={handleAcceptAll}
                className="px-6 py-3 rounded-xl text-sm font-bold text-white bg-steel-blue hover:bg-steel-blue/90 transition-colors shadow-lg shadow-steel-blue/20"
              >
                Accepter alle
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
